import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Search } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { useMarketAreas, findAreaBySlug, toSlug, AREA_EVENT } from "@/lib/marketAreas";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import MarketAreaDrawer from "@/components/market/MarketAreaDrawer";
import { T, TOP, fmtMonth, scrollToId, useIsMobile, useIntelCurrency } from "./intel";
import ActivityMatrix, { Legend } from "./ActivityMatrix";
import MobileActivity from "./MobileActivity";
import AreaDetailPanel from "./AreaDetailPanel";
import PriceBands from "./PriceBands";
import PriceMovement from "./PriceMovement";
import AreaSeoHead from "./AreaSeoHead";

const TAB_TARGETS = ["intel-matrix", "intel-price-bands", "intel-price-movement"];

function Tabs({ t }) {
  const [active, setActive] = useState(0);
  return (
    <nav className="mt-4 sm:mt-6 grid grid-cols-3 sm:flex gap-1.5 sm:gap-2" aria-label={t.title} data-testid="intel-tabs">
      {t.tabs.map((label, i) => (
        <button key={label} type="button" onClick={() => { setActive(i); scrollToId(TAB_TARGETS[i]); }} aria-current={i === active ? "true" : undefined}
          className={`min-h-10 px-2 sm:px-5 py-1.5 rounded-full text-[11.5px] sm:text-[13px] leading-tight border transition-colors ${i === active ? "bg-propertic-navy text-propertic-paper border-propertic-navy" : "bg-white/70 border-propertic-hairline-2 hover:border-propertic-gold"}`}
          data-testid={`intel-tab-${TAB_TARGETS[i]}`}>{label}</button>
      ))}
    </nav>
  );
}

function Controls({ t, metric, setMetric, query, setQuery, isMobile }) {
  return (
    <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4">
      <div className="grid grid-cols-3 rounded-full border hairline bg-white p-1" role="radiogroup" aria-label={t.metric} data-testid="matrix-metric-toggle">
        {Object.entries(t.metrics).map(([k, v]) => (
          <button key={k} type="button" role="radio" aria-checked={metric === k} onClick={() => setMetric(k)}
            className={`h-9 px-2 md:px-4 rounded-full text-[11.5px] md:text-[13px] font-semibold whitespace-nowrap transition-colors ${metric === k ? "bg-propertic-navy text-propertic-paper" : "text-propertic-ink-2 hover:bg-propertic-sand"}`}
            data-testid={`matrix-metric-${k}`}>{v}</button>
        ))}
      </div>
      <div className="relative flex-1">
        <Search className="w-4 h-4 absolute top-3 start-3.5 text-propertic-muted" aria-hidden="true" />
        <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={isMobile ? t.searchShort : t.search} aria-label={t.search}
          className="w-full h-11 rounded-full border hairline bg-white ps-10 pe-4 text-sm focus:outline-none focus:ring-2 focus:ring-propertic-gold" data-testid="matrix-area-search" />
      </div>
    </div>
  );
}

function useRows(data, curated, isAR) {
  return useMemo(() => (data?.curated || []).map((c) => {
    const facts = curated.find((a) => a.area === c.market_area) || null;
    return { ...c, key: c.market_area, slug: toSlug(c.market_area), name: c.market_area, name_ar: facts?.area_ar, label: isAR ? facts?.area_ar || c.market_area : c.market_area, facts };
  }), [data, curated, isAR]);
}

export default function MarketActivity() {
  const { lang } = useLang();
  const isAR = lang === "ar";
  useIntelCurrency();
  const t = T[isAR ? "ar" : "en"];
  const { slug: urlSlug } = useParams();
  const isMobile = useIsMobile();
  const { areas: curated, loaded } = useMarketAreas();
  const [data, setData] = useState(null);
  const [metric, setMetricRaw] = useState("count");
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [sel, setSel] = useState({ key: null, month: null });
  const [sheet, setSheet] = useState(false);
  const [report, setReport] = useState(null);

  useEffect(() => {
    api.get("/market/activity").then((r) => setData(r.data)).catch(() => setData(false));
  }, []);
  const rows = useRows(data, curated, isAR);
  const allMonths = data?.months || [];
  const partial = data?.partial?.month || null;
  const completeMonths = allMonths.filter((m) => m !== partial);

  useEffect(() => {
    if (!rows.length || sel.key) { return; }
    const fromSlug = urlSlug && rows.find((r) => r.slug === urlSlug.toLowerCase());
    setSel({ key: fromSlug ? fromSlug.key : rows[0].key, month: completeMonths[completeMonths.length - 1] });
  }, [rows, urlSlug, completeMonths, sel.key]);

  const months = metric === "change" ? completeMonths.slice(1) : allMonths;
  const setMetric = (m) => {
    setMetricRaw(m);
    if (m === "change" && (sel.month === allMonths[0] || sel.month === partial)) { setSel((s) => ({ ...s, month: completeMonths[completeMonths.length - 1] })); }
  };
  const onSelect = (patch) => {
    setSel((s) => ({ ...s, ...patch }));
    const row = patch.key && rows.find((r) => r.key === patch.key);
    if (row?.facts) {
      window.dispatchEvent(new CustomEvent(AREA_EVENT, { detail: row.facts }));
      window.history.replaceState(null, "", `/dubai-market/${row.slug}${window.location.search}${window.location.hash}`);
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q ? rows.filter((r) => [r.name, r.name_ar || "", ...r.members.map((m) => `${m.area} ${m.area_ar}`)].some((s) => s.toLowerCase().includes(q)) || (q === "jvc" && r.key === "Jumeirah Village Circle") || (q === "jvt" && r.key === "Jumeirah Village Triangle")) : rows;
    const m = sel.month || allMonths[allMonths.length - 1];
    return [...list].sort((a, b) => (metric === "value" ? b.values[m] - a.values[m] : b.counts[m] - a.counts[m]));
  }, [rows, query, metric, sel.month, allMonths]);
  const shown = expanded || query ? filtered : filtered.slice(0, TOP);
  const row = rows.find((r) => r.key === sel.key);
  const latest = completeMonths[completeMonths.length - 1];
  const cov = data && sel.month && data.totals[sel.month].sales_count ? (100 * data.curated_coverage[sel.month].sales_count) / data.totals[sel.month].sales_count : null;
  const invalidSlug = Boolean(urlSlug && loaded && curated.length && !findAreaBySlug(curated, urlSlug));

  const panel = (onClose) => (
    <AreaDetailPanel row={row} months={allMonths} month={sel.month} partial={data.partial} onMonth={(m) => setSel((s) => ({ ...s, month: m }))} onClose={onClose}
      onReport={() => setReport({ ...row.facts, sparkRaw: row.facts.spark })} refreshedAt={data.refreshed_at} isAR={isAR} />
  );
  const view = { rows: shown, months, allMonths, metric, sel, onSelect, partial, isAR, t };

  return (
    <section className="pb-6 sm:pb-10" data-testid="market-activity">
      <AreaSeoHead selected={row?.facts || (urlSlug && findAreaBySlug(curated, urlSlug))} urlSlug={urlSlug} invalidSlug={invalidSlug} lang={lang} />
      <div id="market" className="mx-auto max-w-[1480px] px-3 sm:px-6 lg:px-8 scroll-mt-24">
        <p className="font-mono-brand text-propertic-gold text-[10px] tracking-[0.24em] uppercase">{t.kicker}</p>
        <h2 className="font-editorial text-xl sm:text-3xl lg:text-4xl text-propertic-navy mt-1.5 sm:mt-2 leading-tight">{t.title}</h2>
        <p className="hidden sm:block mt-2 text-[15px] text-propertic-ink-2/90 max-w-3xl">{t.intro}</p>
        <Tabs t={t} />
        {data === null && <p className="mt-6 text-sm text-propertic-muted" data-testid="market-activity-loading">{t.loading}</p>}
        {data === false && <p className="mt-6 text-sm text-propertic-muted" data-testid="market-activity-unavailable">{t.unavailable}</p>}
        {data && sel.month && (
          <>
            <div id="intel-matrix" className="mt-6 grid lg:grid-cols-[minmax(0,1fr)_300px] rounded-[1.5rem] border hairline bg-white/80 overflow-hidden scroll-mt-24 shadow-[0_12px_40px_rgba(10,37,64,0.06)]">
              <div className="p-3.5 sm:p-6 min-w-0">
                <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
                  <h3 className="font-editorial text-xl sm:text-2xl text-propertic-navy">{t.matrix}</h3>
                  <span className="text-[11px] font-mono-brand tracking-[0.1em] uppercase text-propertic-muted" data-testid="matrix-latest-month">{t.latest(fmtMonth(latest, isAR))}</span>
                </div>
                <Controls t={t} metric={metric} setMetric={setMetric} query={query} setQuery={setQuery} isMobile={isMobile} />
                {isMobile ? <MobileActivity {...view} onOpen={() => setSheet(true)} /> : <ActivityMatrix {...view} />}
                <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                  <p className="text-[11px] text-propertic-muted" data-testid="matrix-showing">{t.showing(shown.length, filtered.length)}</p>
                  {!query && filtered.length > TOP && (
                    <button type="button" onClick={() => setExpanded((e) => !e)} className="h-10 px-5 rounded-full border border-propertic-navy/30 hover:border-propertic-gold text-[11px] font-mono-brand tracking-[0.14em] uppercase" data-testid="matrix-show-more">
                      {expanded ? t.less : t.more(filtered.length)}
                    </button>
                  )}
                </div>
                <Legend t={t} partial={partial && metric !== "change"} note={metric === "change" ? t.changeNote(fmtMonth(allMonths[1], isAR)) : null} />
                {cov != null && <p className="mt-2 text-[11px] text-propertic-muted" data-testid="matrix-coverage">{t.coverage(isAR ? `${cov.toFixed(1).replace(/[0-9]/g, (d) => "٠١٢٣٤٥٦٧٨٩"[+d])}٪` : `${cov.toFixed(1)}%`, fmtMonth(sel.month, isAR))}</p>}
              </div>
              {!isMobile && <aside className="border-t lg:border-t-0 lg:border-s hairline bg-propertic-paper-2 p-5 sm:p-6" aria-live="polite">{panel(null)}</aside>}
            </div>
            <div className="mt-6 grid lg:grid-cols-2 gap-6 items-stretch">
              <PriceBands month={sel.month} rows={rows} isAR={isAR} />
              <PriceMovement rows={rows} areaKey={sel.key} setAreaKey={(k) => onSelect({ key: k })} month={sel.month} isAR={isAR} />
            </div>
          </>
        )}
      </div>
      {isMobile && data && (
        <Drawer open={sheet} onOpenChange={setSheet} shouldScaleBackground={false}>
          <DrawerContent className="bg-propertic-paper-2 max-h-[88vh]" dir={isAR ? "rtl" : "ltr"} data-testid="mobile-area-sheet">
            <DrawerTitle className="sr-only">{row?.label}</DrawerTitle>
            <div className="overflow-y-auto px-5 pb-8 pt-2">{panel(() => setSheet(false))}</div>
          </DrawerContent>
        </Drawer>
      )}
      <MarketAreaDrawer area={report} onClose={() => setReport(null)} />
    </section>
  );
}
